import React, { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import type { VerifyHumanityOutput } from '@zealy/schemas';
import { Alert, Button, TaskCard } from '@zealy/design-system';
import { ShieldLine } from '@zealy/icons';
import { useVerifyHumanity } from '@zealy/queries/src/antibot';

import { useError } from '#app/cw/[subdomain]/(app)/questboard/admin/[questId]/_components/FormError.context';
import { TASK_COLORS } from '#constants/quests/TaskColors';

import type { QuestTask } from '../Tasks.types';
import { withQuest } from '../Tasks.funcs';

type WrapperProps = Extract<QuestTask, { type: 'proveYourHumanity' }>;

const type = 'proveYourHumanity' as const;
type Task = typeof type;

export const ProveYourHumanity = ({
  task,
  preview = false,
}: { task: WrapperProps } & { preview?: boolean }) => {
  const t = useTranslations();
  const { formState } = useFormContext();
  const [verificationResult, setVerificationResult] = useState<{
    isHuman?: boolean;
    reason?: string;
    suspiciousField?: string;
  } | null>(null);

  const { clearError, error, setError } = useError(
    `${task.id}.verification`,
    !preview && task.id && !verificationResult?.isHuman ? 'Verification Required' : undefined,
  );

  const { mutate: verifyHumanity, isPending } = useVerifyHumanity();

  useEffect(() => {
    if (!task.id) return;

    if (error && verificationResult?.isHuman === true) {
      clearError();
    } else if (!error && verificationResult?.isHuman === false) {
      setError(verificationResult.reason || 'Verification Failed');
    }
  }, [verificationResult, task.id, setError, error, clearError]);

  const handleVerify = () => {
    // Clear previous results and errors
    setVerificationResult(null);

    verifyHumanity(undefined, {
      onSuccess: (data: VerifyHumanityOutput) => {
        setVerificationResult(data);
        if (!data.isHuman) {
          console.log('setting error');
          setError(data.reason || 'Verification Failed');
        }
      },
      onError: (error: Error) => {
        // Check if it's a rate limiting error (cached result)
        if (error.message.includes('rate limit') || error.message.includes('cached')) {
          setError(t('quest.type.proveYourHumanity.rateLimitError'));
        } else {
          setError(t('quest.type.proveYourHumanity.genericError'));
        }
      },
    });
  };

  const getButtonText = () => {
    if (isPending) return t('quest.type.proveYourHumanity.verifying');
    if (verificationResult?.isHuman === true) return t('quest.type.proveYourHumanity.verified');
    if (verificationResult?.isHuman === false) return t('quest.type.proveYourHumanity.failed');
    return t('quest.type.proveYourHumanity.button');
  };

  const getButtonVariant = () => {
    if (verificationResult?.isHuman === false) return 'ghost';
    return 'filled';
  };

  const getButtonColor = () => {
    if (verificationResult?.isHuman === false) return 'destructive';
    return 'cta';
  };

  const getButtonClassName = () => {
    // Use the task-specific color from TaskColors.ts
    const baseClasses = `w-full ${TASK_COLORS.proveYourHumanity} hover:bg-task-type-api-hover active:bg-task-type-api-press`;

    // Override with destructive styling if verification failed
    if (verificationResult?.isHuman === false) {
      return `${baseClasses} !bg-error-primary`;
    }

    return baseClasses;
  };

  return (
    <TaskCard
      type="proveYourHumanity"
      title={t('quest.type.proveYourHumanity.label')}
      icon={ShieldLine}
    >
      <div className="flex flex-col gap-300 px-200 py-400">
        <p className="body-paragraph-lg text-secondary">
          {t('quest.type.proveYourHumanity.description')}
        </p>

        {error && verificationResult && (
          <Alert
            variant="error"
            title={t('quest.type.proveYourHumanity.verificationFailed')}
            description={
              'Reason: ' + error + ' ' + t('quest.type.proveYourHumanity.retryInOneHour')
            }
          />
        )}

        {verificationResult?.isHuman === true && (
          <Alert variant="success" title={t('quest.type.proveYourHumanity.verificationSuccess')} />
        )}

        <Button
          leftIcon={<ShieldLine />}
          size="lg"
          className={getButtonClassName()}
          variant={getButtonVariant()}
          color={getButtonColor()}
          onClick={handleVerify}
          isDisabled={preview || isPending || !!verificationResult}
          loading={isPending}
        >
          {getButtonText()}
        </Button>
      </div>
    </TaskCard>
  );
};

const ProveYourHumanityClaim = withQuest<WrapperProps, Task>(({ task }) => {
  const { field } = useController({
    name: task.id,
    defaultValue: {
      taskId: task.id,
      type,
    },
  });

  // The field is already registered with the form, no need to explicitly use it
  // The form will collect the value when submitting
  return <ProveYourHumanity task={task} />;
});

export default ProveYourHumanityClaim;
